import { useModal } from 'lib/components/Modal/Modal'
import { SMS_ONLY_MODAL_ID, SMS_OPT_IN_MODAL_ID } from 'lib/components/SmsOptInModal/SmsOptInModal'

import { isSmsMarketingEnabled } from '@/hooks/useSmsMarketingEnabled'
import { getCookie } from '@/utils/cookies'
import { isSmsMarketingSubscribed } from '@/utils/smsSubscriptionState'

// Hold before the footer SMS modal opens, so the inline success state in the
// footer registers before the modal takes over the screen. The Braze surface
// gets no equivalent hold — see openSmsOnlyModal.
const FOOTER_QUIET_MS = 1500

// Hold before the return-visit SMS modal auto-opens on page load (the
// ClientPortal replacement path, where the Braze lead-capture cookie is already
// set). Popping the instant Braze initializes reads as a load artifact; this
// lets the page settle first.
export const RETURN_VISIT_QUIET_MS = 8000

// Hold before the SMS opt-in opens after a Braze email lead capture is
// submitted. The modal is opened on this fixed timer instead of waiting on the
// lead POST (postCreateWelcomeLead / createWelcomeLead), which is slow in
// production — so it appears promptly regardless of the API round-trip.
export const POST_CAPTURE_QUIET_MS = 200

// Dedupe cookie for the SMS-only modal — twin of the Braze lead-capture cookie
// (braze_lead_capture_shown). Once the SMS-only modal has been shown, it does
// not auto-open again for a year, across every trigger path (the post-capture
// handoff and the return-visit replacement in ClientPortal). Key/expiry stay in
// sync with the Braze cookie style (max-age + samesite=lax).
export const SMS_ONLY_MODAL_SHOWN_COOKIE = 'sms_only_modal_shown'

// Post-purchase / order pages where the promotional Braze email modal and the
// SMS-only popup are suppressed, so a promo never pops over a customer right
// after checkout or mid order-lookup. These pages keep their own opt-in
// surfaces (order-confirmation's inline card, order-tracking's transactional
// signup). Consumed by ClientPortal (Braze email gate) and openSmsOnlyModal.
export const PROMO_MODAL_SUPPRESSED_ROUTES = ['/order-confirmation', '/order-tracking']

export const isPromoModalSuppressedPath = (pathname: string): boolean =>
  PROMO_MODAL_SUPPRESSED_ROUTES.some((route) => pathname.startsWith(route))

/**
 * Standalone SMS-only opt-in. Opened once a Braze email capture is confirmed,
 * and — for return visitors whose Braze lead-capture cookie is already set (so
 * the Braze modal no longer fires) — in place of the Braze modal on load.
 *
 * This modal *replaces* Braze's own confirmation screen rather than queueing
 * behind it: callers close the Braze message at submit (see
 * closeActiveBrazeInAppMessage) and open this once the lead POST succeeds.
 * That ordering leaves a brief empty beat while the POST is in flight, which
 * is the deliberate trade for never surfacing the opt-in behind a failed
 * capture. No delay is added here beyond that round-trip.
 *
 * `email` seeds the opt-in for identity stitching when known (the post-capture
 * handoff); it is omitted on the return-visit path, where the modal still works
 * and just collects the phone number.
 *
 * Shows at most once per browser: guarded by SMS_ONLY_MODAL_SHOWN_COOKIE, which
 * it sets when it actually opens.
 *
 * Never opens for a user who is already subscribed for this brand
 * (GSE-12330) — including an opt-in made while logged out that the backend
 * has since tied to the account by its phone number. That check runs after
 * the brand flag and before the cookie, so the once-a-year showing is not
 * spent on a modal that never opened.
 */
export const openSmsOnlyModal = (email?: string): void => {
  if (typeof window === 'undefined') return
  // Never surface the SMS-only popup on the order pages, regardless of which
  // path triggered it (return-visit timer or post-capture handoff).
  if (isPromoModalSuppressedPath(window.location.pathname)) {
    return
  }
  if (!isSmsMarketingEnabled()) {
    return
  }
  if (isSmsMarketingSubscribed()) {
    // eslint-disable-next-line no-console
    console.log('[sms-timing] openSmsOnlyModal skipped: already subscribed')
    return
  }
  if (getCookie(SMS_ONLY_MODAL_SHOWN_COOKIE)) {
    return
  }

  const oneYear = 60 * 60 * 24 * 365
  document.cookie = `${SMS_ONLY_MODAL_SHOWN_COOKIE}=1; max-age=${oneYear}; path=/; samesite=lax`

  useModal.getState().openDialog(SMS_ONLY_MODAL_ID, { email })
}

/**
 * Footer SMS opt-in, queued after a successful newsletter email signup. The
 * footer form is the project's own — there is no overlay being replaced — so
 * this holds briefly rather than opening on top of the success state.
 * Skipped for a user who is already subscribed (see openSmsOnlyModal).
 */
export const queueFooterSmsOptInModal = (email?: string): void => {
  if (typeof window === 'undefined') return
  if (!isSmsMarketingEnabled()) return
  if (isSmsMarketingSubscribed()) return

  window.setTimeout(() => {
    useModal.getState().openDialog(SMS_OPT_IN_MODAL_ID, { email })
  }, FOOTER_QUIET_MS)
}
